<script>
    import { createEventDispatcher, onMount } from 'svelte';
    import { SpinningWheelCategory } from '../spinning-wheel-item.model';

    const dispatch = createEventDispatcher();

    export let playerName = 'Player';
    export let data = [];

    let spinFinished = false;
    let picked = 0;
    let rotationDegrees = 0;
    let autoCloseTimer = null;
    let countdownSec = 3;
    let countdownInterval = null;

    // 4 vibrant theme colors for the slices
    const sliceColors = [
        { bg: '#005d46', text: '#ffffff' }, // Dark Forest Green
        { bg: '#eba9ff', text: '#3b136f' }, // Pink
        { bg: '#6434da', text: '#ffffff' }, // Purple
        { bg: '#21e467', text: '#004d3a' }  // Light Neon Green
    ];

    const cx = 200;
    const cy = 200;
    const radius = 175;

    $: numSlices = data && data.length > 0 ? data.length : 1;
    $: sliceAngle = 360 / numSlices;

    function getSliceColor(index) {
        return sliceColors[index % sliceColors.length].bg;
    }

    function getSliceTextColor(index) {
        return sliceColors[index % sliceColors.length].text;
    }

    function getSlicePath(index) {
        const startAngle = index * sliceAngle;
        const endAngle = (index + 1) * sliceAngle;
        
        const startRad = (startAngle - 90) * (Math.PI / 180);
        const endRad = (endAngle - 90) * (Math.PI / 180);

        const x1 = cx + radius * Math.cos(startRad);
        const y1 = cy + radius * Math.sin(startRad);
        const x2 = cx + radius * Math.cos(endRad);
        const y2 = cy + radius * Math.sin(endRad);

        const largeArcFlag = sliceAngle > 180 ? 1 : 0;

        return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
    }

    function getSliceMidAngle(index) {
        return (index + 0.5) * sliceAngle;
    }

    function truncateLabel(str, maxLen = 20) {
        if (!str) return '';
        return str.length > maxLen ? str.slice(0, maxLen - 1) + '…' : str;
    }

    function finishSpin() {
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
        if (countdownInterval) clearInterval(countdownInterval);

        if (data && data[picked]) {
            dispatch('selectedSpin', {
                event: data[picked].event
            });
        }
    }

    onMount(() => {
        if (!data || data.length === 0) return;

        // Pick random item
        picked = Math.floor(Math.random() * data.length);

        // Center of picked slice in degrees (from 12 o'clock)
        const pickedCenterAngle = (picked + 0.5) * sliceAngle;

        // Number of full rotations
        const fullSpins = 5 + Math.floor(Math.random() * 2);
        
        // Target angle: rotate so picked slice center is at 12 o'clock (0 degrees)
        const targetRotation = (fullSpins * 360) + (360 - pickedCenterAngle);

        // Start animation slightly after mount
        const spinTimeout = setTimeout(() => {
            rotationDegrees = targetRotation;
        }, 80);

        // Wait for spin duration (3.5s) to complete
        const finishTimeout = setTimeout(() => {
            spinFinished = true;

            // Start countdown
            countdownSec = 4;
            countdownInterval = setInterval(() => {
                countdownSec -= 1;
                if (countdownSec <= 0) {
                    clearInterval(countdownInterval);
                }
            }, 1000);

            // Auto dispatch after reveal
            autoCloseTimer = setTimeout(() => {
                finishSpin();
            }, 4000);
        }, 3600);

        return () => {
            clearTimeout(spinTimeout);
            clearTimeout(finishTimeout);
            if (autoCloseTimer) clearTimeout(autoCloseTimer);
            if (countdownInterval) clearInterval(countdownInterval);
        };
    });
</script>

<div class="w-full flex flex-col items-center select-none text-slate-800 p-4 sm:p-6 min-h-[480px] justify-center relative">
    <!-- Close button handled by parent modal or internal finish -->

    {#if !spinFinished}
        <!-- Header Badge & Player Title -->
        <div class="flex flex-col items-center text-center mb-3">
            <h2 class="text-xl sm:text-2xl md:text-3xl font-black text-[#005d46] font-headline tracking-tight">
                Are you feeling lucky, {playerName}?
            </h2>
            <p class="text-xs sm:text-sm text-slate-500 mt-1 flex items-center justify-center gap-2">
                <span class="material-symbols-outlined text-[16px] text-[#005d46] animate-spin">progress_activity</span>
                <span>The wheel is spinning...</span>
            </p>
        </div>

        <!-- Spinning Wheel Visual -->
        <div class="relative flex items-center justify-center my-2">
            <!-- Pointer Arrow at Top (12 o'clock) -->
            <div class="absolute -top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none drop-shadow-md">
                <svg class="w-8 h-8 sm:w-9 sm:h-9" viewBox="0 0 32 32" fill="none">
                    <path d="M16 26L7 7C6.5 6 7.2 4.8 8.4 4.8H23.6C24.8 4.8 25.5 6 25 7L16 26Z" fill="#21e467" stroke="#005d46" stroke-width="2.5" stroke-linejoin="round"/>
                    <circle cx="16" cy="10" r="3" fill="#005d46" />
                </svg>
            </div>

            <!-- Wheel Outer Glow & Border Container -->
            <div class="relative p-2 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border-4 border-white shadow-xl">
                <!-- SVG Rotating Wheel -->
                <svg
                    class="w-[280px] h-[280px] sm:w-[340px] sm:h-[340px] md:w-[360px] md:h-[360px] rounded-full wheel-svg"
                    viewBox="0 0 400 400"
                    style="transform: rotate({rotationDegrees}deg); transition: transform 3.5s cubic-bezier(0.12, 0.9, 0.2, 1);"
                >
                    <g>
                        {#each data as item, i}
                            <!-- Slice Segment -->
                            <path
                                d="{getSlicePath(i)}"
                                fill="{getSliceColor(i)}"
                                stroke="#ffffff"
                                stroke-width="2"
                            />
                            
                            <!-- Slice Label Text -->
                            <g transform="rotate({getSliceMidAngle(i)}, {cx}, {cy}) translate(0, -{radius * 0.58})">
                                <text
                                    class="select-none font-headline font-black text-[12px] sm:text-[13px]"
                                    text-anchor="middle"
                                    dominant-baseline="central"
                                    fill="{getSliceTextColor(i)}"
                                    transform="rotate(90)"
                                    style="letter-spacing: -0.01em;"
                                >
                                    {truncateLabel(item.label, 20)}
                                </text>
                            </g>
                        {/each}
                    </g>

                    <!-- Center Hub Ring -->
                    <circle cx="{cx}" cy="{cy}" r="34" fill="#ffffff" stroke="#005d46" stroke-width="4" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.15))"/>
                    <circle cx="{cx}" cy="{cy}" r="26" fill="#005d46" />
                    <circle cx="{cx}" cy="{cy}" r="6" fill="#21e467" />
                </svg>
            </div>
        </div>
    {:else}
        <div class="w-full flex flex-col items-center justify-center text-center py-6 px-4 animate-result-pop">
            <div class="my-4 px-2 w-full max-w-xl">
                <h1 class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-headline tracking-tight leading-none {
                    data[picked]?.category === SpinningWheelCategory.BAD
                        ? 'text-rose-600'
                        : data[picked]?.category === SpinningWheelCategory.LUCKY
                            ? 'text-[#005d46]'
                            : 'text-[#6434da]'
                }">
                    {data[picked]?.label}
                </h1>
            </div>

            <p class="text-xs text-slate-400 mt-4">
                Starting automatically in a few seconds...
            </p>
        </div>
    {/if}
</div>

<style>
    @keyframes resultPop {
        0% {
            opacity: 0;
            transform: scale(0.92) translateY(8px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }

    .animate-result-pop {
        animation: resultPop 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
</style>
